/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import i18n.LocaleManager
import i18n.StringKey
import i18n.tr
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLElement

/**
 * Landing page component for Wiz3r web app
 * Centered around the "One Number" savings percentage philosophy
 */
class LandingPage(
    private val onLoginClick: () -> Unit,
    private val onGetStartedClick: () -> Unit,
    private val onTermsClick: () -> Unit = {},
    private val onPrivacyClick: () -> Unit = {},
    private val onSecurityClick: () -> Unit = {},
    private val onAboutClick: () -> Unit = {},
    private val onContactClick: () -> Unit = {},
    private val onTeamClick: () -> Unit = {},
    private val onGuideClick: () -> Unit = {},
    private val isLoggedIn: Boolean = false,
    private val onDashboardClick: () -> Unit = {},
    private val onNavClick: ((String) -> Unit)? = null,
    private val onSettingsClick: (() -> Unit)? = null,
    private val onSubscribeClick: (() -> Unit)? = null,
    private val onSubscribeMenuClick: (() -> Unit)? = null,
    private val onLogout: (() -> Unit)? = null,
) {
    private val isMobile = ResponsiveUtils.isMobile()

    fun render(): HTMLElement {
        val container = document.createElement("div") as HTMLElement
        container.style.cssText = """
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
            background: transparent;
            color: #ffffff;
            min-height: 100vh;
        """

        container.innerHTML = buildLandingPageHTML()

        return container
    }

    private fun buildLandingPageHTML(): String {
        val headerPadding = if (isMobile) "12px 16px" else "16px 48px"
        val logoHeight = if (isMobile) "40px" else "56px"

        // Mobile menu overlay HTML (only for logged-in mobile users)
        val mobileOverlayHtml = if (isMobile && isLoggedIn) """
            <div id="mobileMenuOverlay" style="display: none; position: fixed; inset: 0; background: rgba(0,0,0,0.95); z-index: 200;">
                <div style="padding: 20px;">
                    <div style="display: flex; justify-content: flex-end; margin-bottom: 20px;">
                        <button id="mobileMenuClose" style="background: none; border: none; color: #fff; font-size: 24px; cursor: pointer;">✕</button>
                    </div>
                    <div style="display: flex; flex-direction: column; gap: 16px;">
                        <span id="mobileNavActivity" style="padding: 16px; border-radius: 8px; font-size: 16px; font-weight: 500; cursor: pointer; background: transparent; color: #888;">${StringKey.NAV_TRANSACTIONS.tr()}</span>
                        <span id="mobileNavDashboard" style="padding: 16px; border-radius: 8px; font-size: 16px; font-weight: 500; cursor: pointer; background: transparent; color: #888;">${StringKey.NAV_DASHBOARD.tr()}</span>
                        <span id="mobileNavGoals" style="padding: 16px; border-radius: 8px; font-size: 16px; font-weight: 500; cursor: pointer; background: transparent; color: #888;">${StringKey.NAV_GOALS.tr()}</span>
                        <span id="mobileNavBudget" style="padding: 16px; border-radius: 8px; font-size: 16px; font-weight: 500; cursor: pointer; background: transparent; color: #888;">${StringKey.NAV_BUDGET.tr()}</span>
                        <div style="border-top: 1px solid #333; margin-top: 8px; padding-top: 16px;">
                            <span id="mobileNavSettings" style="padding: 16px; border-radius: 8px; font-size: 16px; font-weight: 500; cursor: pointer; background: transparent; color: #888; display: flex; align-items: center; gap: 8px;">
                                <span>⚙</span> Settings
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        """ else ""

        val marketingMenuHtml = if (!isMobile) MarketingMenu.hamburgerButtonHtml() + MarketingMenu.overlayHtml(currentPage = "landing", isLoggedIn = isLoggedIn) else ""

        return """
            $mobileOverlayHtml
            $marketingMenuHtml
            <!-- Header -->
            <div id="landing-header" style="
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: $headerPadding;
                position: sticky;
                top: 0;
                background: rgba(10, 10, 10, 0.95);
                backdrop-filter: blur(10px);
                border-bottom: 1px solid #1a1a1a;
                z-index: 100;
                transition: padding 0.3s ease;
            ">
                <!-- Logo -->
                <img id="header-logo" src="/wiz3r_logo.png?v=3" alt="wiz≡r" style="
                    height: $logoHeight;
                    cursor: pointer;
                    transition: height 0.3s ease;
                ">
                <!-- Navigation -->
                <div style="display: flex; align-items: center; gap: ${if (isMobile) "12px" else "32px"}; ${if (!isMobile) "margin-right: 56px;" else ""}">
                    <span id="landing-language" style="padding: 4px 8px; border-radius: 4px; background: #1a1a1a; color: #00d9ff; font-size: 12px; font-weight: 600; cursor: pointer; border: 1px solid #333; transition: all 0.2s;" title="${StringKey.TOOLTIP_CHANGE_LANGUAGE.tr()}">${languageToggleLabel()}</span>
                    ${if (isLoggedIn) {
                        if (isMobile) {
                            // Mobile logged in - show hamburger menu
                            """
                            <button id="hamburgerMenu" style="background: none; border: none; color: #fff; font-size: 24px; cursor: pointer; padding: 4px;">☰</button>
                            """
                        } else {
                            // Desktop logged in - show dashboard link
                            """
                            <span id="dashboard-link" style="
                                display: flex;
                                align-items: center;
                                gap: 8px;
                                color: #ffffff;
                                cursor: pointer;
                                font-size: 15px;
                                transition: all 0.2s;
                                padding: 8px 16px;
                                border-radius: 8px;
                                background: rgba(0, 217, 255, 0.1);
                                border: 1px solid rgba(0, 217, 255, 0.3);
                            ">
                                <span style="width: 28px; height: 28px; border-radius: 50%; background: #00d9ff; display: flex; align-items: center; justify-content: center; color: #0a0a0a; font-size: 14px;">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                                    </svg>
                                </span>
                                <span>${StringKey.LANDING_MY_DASHBOARD.tr()}</span>
                            </span>
                            """
                        }
                    } else {
                        // Not logged in - show Login and Get Started on both mobile and desktop
                        if (isMobile) {
                            """
                            <span id="login-link" style="color: #ffffff; cursor: pointer; font-size: 14px; padding: 8px 12px; transition: color 0.2s;">${StringKey.AUTH_LOGIN.tr()}</span>
                            <span id="get-started-btn" style="background: #00d9ff; color: #0a0a0a; padding: 8px 16px; border-radius: 8px; font-weight: 600; font-size: 14px; cursor: pointer; transition: all 0.2s;">${StringKey.LANDING_GET_STARTED.tr()}</span>
                            """
                        } else {
                            """
                            <span id="login-link" style="color: #ffffff; cursor: pointer; font-size: 15px; transition: color 0.2s;">${StringKey.AUTH_LOGIN.tr()}</span>
                            <span id="get-started-btn" style="background: #00d9ff; color: #0a0a0a; padding: 10px 24px; border-radius: 8px; font-weight: 600; font-size: 15px; cursor: pointer; transition: all 0.2s;">${StringKey.LANDING_GET_STARTED.tr()}</span>
                            """
                        }
                    }}
                </div>
            </div>

            <!-- Hero Section with cloud backdrop -->
            <style>
                /* Philosophy cards — scroll-in + hover */
                .philosophy-card {
                    background: #141414;
                    border-radius: 16px;
                    padding: 32px;
                    border: 1px solid #1f1f1f;
                    opacity: 0;
                    transition: transform 0.4s cubic-bezier(.2,.8,.2,1), box-shadow 0.3s ease, border-color 0.3s ease;
                    will-change: transform, opacity;
                }
                .philo-left   { transform: translateX(-120px); }
                .philo-right  { transform: translateX(120px); }
                .philo-middle { transform: scale(0.3); }
                #philosophy-grid.animate .philo-left,
                #philosophy-grid.animate .philo-right {
                    opacity: 1;
                    transform: translateX(0);
                    transition: opacity 0.8s ease-out, transform 0.8s cubic-bezier(.2,.8,.2,1);
                }
                #philosophy-grid.animate .philo-middle {
                    opacity: 1;
                    transform: scale(1);
                    transition: opacity 0.6s ease-out 0.2s, transform 0.7s cubic-bezier(.2,.8,.2,1) 0.2s;
                }
                .philosophy-card:hover {
                    transform: scale(1.05) !important;
                    box-shadow: 0 12px 40px rgba(0, 217, 255, 0.2);
                    border-color: rgba(0, 217, 255, 0.4);
                }
                .philo-icon {
                    transition: transform 1.2s cubic-bezier(.2,.8,.2,1);
                    transform-style: preserve-3d;
                }
                .philosophy-card:hover .philo-icon {
                    transform: rotateY(180deg);
                }

                /* Features cards — flip-up 3D entrance, icon pulse on hover */
                .feature-card {
                    background: #141414;
                    border-radius: 16px;
                    padding: 32px;
                    border: 1px solid #1f1f1f;
                    opacity: 0;
                    transform: perspective(1000px) rotateX(60deg) translateY(80px);
                    transform-origin: bottom center;
                    transition: transform 0.3s cubic-bezier(.2,.8,.2,1), box-shadow 0.3s ease, border-color 0.3s ease;
                    will-change: transform, opacity;
                }
                #features-grid.animate .feature-card {
                    opacity: 1;
                    transform: perspective(1000px) rotateX(0deg) translateY(0);
                    transition: opacity 0.8s ease-out, transform 0.9s cubic-bezier(.2,.8,.2,1), box-shadow 0.3s ease, border-color 0.3s ease;
                }
                #features-grid.animate .feature-card:nth-child(1) { transition-delay: 0s; }
                #features-grid.animate .feature-card:nth-child(2) { transition-delay: 0.15s; }
                #features-grid.animate .feature-card:nth-child(3) { transition-delay: 0.3s; }
                .feature-card:hover {
                    transform: scale(1.05) !important;
                    box-shadow: 0 12px 40px rgba(0, 217, 255, 0.2);
                    border-color: rgba(0, 217, 255, 0.4);
                }
                .feature-icon {
                    transition: transform 0.6s cubic-bezier(.34,1.56,.64,1);
                }
                .feature-card:hover .feature-icon {
                    animation: iconWiggle 0.8s ease-in-out;
                }
                @keyframes iconWiggle {
                    0%   { transform: scale(1)    rotate(0deg); }
                    20%  { transform: scale(1.25) rotate(-12deg); }
                    40%  { transform: scale(1.25) rotate(12deg); }
                    60%  { transform: scale(1.25) rotate(-8deg); }
                    80%  { transform: scale(1.15) rotate(5deg); }
                    100% { transform: scale(1)    rotate(0deg); }
                }

                /* Advice ribbon — continuous marquee scroll, pause/zoom on hover.
                   overflow-x: clip + overflow-y: visible lets the scaled card and
                   its box-shadow halo spill above/below without losing horizontal
                   clipping at the ribbon edges. Plain `overflow: hidden` would
                   create a containing block that forces vertical clipping too,
                   which truncates the hover state. Padding-y reserves space for
                   the scaled card so adjacent sections aren't pushed around. */
                .advice-ribbon {
                    scrollbar-width: none;
                    overflow-x: clip;
                    overflow-y: visible;
                    padding: 18px 0;
                }
                .advice-ribbon::-webkit-scrollbar { display: none; }
                .advice-track {
                    display: flex;
                    gap: 24px;
                    width: max-content;
                    animation: marquee 70s linear infinite;
                    will-change: transform;
                }
                .advice-ribbon:hover .advice-track {
                    animation-play-state: paused;
                }
                @keyframes marquee {
                    from { transform: translateX(0); }
                    to   { transform: translateX(-50%); }
                }
                .advice-card {
                    transition: transform 0.3s cubic-bezier(.2,.8,.2,1), box-shadow 0.3s ease, border-color 0.3s ease;
                    cursor: pointer;
                }
                .advice-card:hover {
                    transform: scale(1.04);
                    box-shadow: 0 12px 40px rgba(0, 217, 255, 0.25);
                    border-color: rgba(0, 217, 255, 0.4) !important;
                }

                /* Pricing cards — 3D flip container with front/back faces */
                .pricing-flipper {
                    opacity: 0;
                    transform: translateY(40px) scale(0.95);
                    transition: opacity 0.7s ease-out, transform 0.7s cubic-bezier(.2,.8,.2,1);
                    position: relative;
                }
                .pricing-standard { transition-delay: 0s; }
                .pricing-pro      { transition-delay: 0.15s; }
                #pricing-grid.animate .pricing-flipper {
                    opacity: 1;
                    transform: translateY(0) scale(1);
                }
                #pricing-grid.animate .pricing-pro {
                    transform: translateY(0) scale(1.03);
                }
                .pricing-flipper:hover {
                    box-shadow: none;
                }
                .pricing-pro:hover {
                    transform: translateY(-6px) scale(1.05) !important;
                }
                .pricing-pro:hover .pricing-face {
                    box-shadow: 0 0 60px rgba(0, 217, 255, 0.5), 0 20px 60px rgba(0, 217, 255, 0.35) !important;
                    animation: proGlowPulse 2s ease-in-out infinite;
                }
                .pricing-standard:hover .pricing-face-front,
                .pricing-standard:hover .pricing-face-back {
                    box-shadow: 0 16px 50px rgba(0, 217, 255, 0.25);
                }
                @keyframes proGlowPulse {
                    0%, 100% { box-shadow: 0 0 60px rgba(0, 217, 255, 0.45), 0 20px 60px rgba(0, 217, 255, 0.3); }
                    50%      { box-shadow: 0 0 80px rgba(0, 217, 255, 0.65), 0 20px 80px rgba(0, 217, 255, 0.45); }
                }
                .pricing-flip-inner {
                    position: relative;
                    width: 100%;
                    display: grid;
                    transform-style: preserve-3d;
                    transition: transform 0.9s cubic-bezier(.4,.2,.2,1);
                }
                .pricing-flipper.flipped .pricing-flip-inner {
                    transform: rotateY(180deg);
                }
                .pricing-face {
                    grid-area: 1 / 1;
                    -webkit-backface-visibility: hidden;
                    backface-visibility: hidden;
                    transition: box-shadow 0.3s ease;
                }
                .pricing-face-back {
                    transform: rotateY(180deg);
                }
                .pro-shimmer {
                    position: absolute;
                    top: 0; left: -100%;
                    width: 60%; height: 100%;
                    background: linear-gradient(110deg, transparent 20%, rgba(0,217,255,0.18) 50%, transparent 80%);
                    pointer-events: none;
                    z-index: 1;
                }
                #pricing-grid.animate .pro-shimmer {
                    animation: shimmerSweep 3.2s ease-in-out 0.8s infinite;
                }
                @keyframes shimmerSweep {
                    0%   { left: -100%; }
                    50%  { left: 120%; }
                    100% { left: 120%; }
                }

                /* Trust section — particle-merge (reverse explosion) */
                .trust-item {
                    position: relative;
                    opacity: 0;
                    transform: scale(0.3);
                    transition: opacity 0.6s ease-out, transform 0.8s cubic-bezier(.2,.8,.2,1);
                }
                #trust-grid.animate .trust-item {
                    opacity: 1;
                    transform: scale(1);
                }
                #trust-grid.animate .trust-item:nth-child(1) { transition-delay: 0.0s; }
                #trust-grid.animate .trust-item:nth-child(2) { transition-delay: 0.15s; }
                #trust-grid.animate .trust-item:nth-child(3) { transition-delay: 0.3s; }
                #trust-grid.animate .trust-item:nth-child(4) { transition-delay: 0.45s; }
                .trust-particle {
                    position: absolute;
                    left: 50%; top: 30%;
                    width: 6px; height: 6px;
                    border-radius: 50%;
                    background: #00d9ff;
                    pointer-events: none;
                    opacity: 0;
                    box-shadow: 0 0 8px rgba(0, 217, 255, 0.8);
                }
                #trust-grid.animate .trust-particle {
                    animation: particleConverge 1.3s ease-out forwards;
                }

                @keyframes particleConverge {
                    0%   { opacity: 0; transform: translate(var(--px), var(--py)) scale(1); }
                    25%  { opacity: 1; }
                    100% { opacity: 0; transform: translate(0, 0) scale(0); }
                }

                /* Mystery percentage — gentle grey pulse on the question marks */
                .mystery-pct { position: relative; display: inline-block; }
                .mystery-char { display: inline-block; transform-origin: center center; }
                .mystery-pct .mystery-char:nth-child(1),
                .mystery-pct .mystery-char:nth-child(2) {
                    animation: mysteryPulse 3.5s ease-in-out infinite;
                }
                .mystery-pct .mystery-char:nth-child(2) { animation-delay: 0.4s; }
                @keyframes mysteryPulse {
                    0%, 100% {
                        transform: scale(1);
                        text-shadow: none;
                    }
                    50% {
                        transform: scale(1.35);
                        text-shadow: 0 0 40px rgba(210, 210, 210, 0.55), 0 0 80px rgba(180, 180, 180, 0.35), 0 0 120px rgba(160, 160, 160, 0.2);
                    }
                }

                /* Pricing Pro — BEST VALUE badge fades in on hover */
                .pro-best-value {
                    position: absolute;
                    top: 14px;
                    right: 14px;
                    background: #00d9ff;
                    color: #000;
                    font-size: 11px;
                    font-weight: 700;
                    padding: 4px 14px;
                    border-radius: 10px;
                    z-index: 2;
                    opacity: 0;
                    transform: translateY(-6px);
                    transition: opacity 0.3s ease-out, transform 0.3s cubic-bezier(.2,.8,.2,1);
                    pointer-events: none;
                }
                .pricing-pro:hover .pro-best-value {
                    opacity: 1;
                    transform: translateY(0);
                }

                @media (prefers-reduced-motion: reduce) {
                    .philosophy-card, .feature-card, .pricing-card, .trust-item, .mystery-char { opacity: 1; transform: none !important; transition: none; filter: none; }
                    .philo-icon, .feature-icon, .advice-card, .advice-track, .pricing-pro, .pro-shimmer, .trust-particle { transition: none; animation: none !important; }
                }

                .hero-wrap { position: relative; overflow: hidden; }
                .cloud-backdrop { position: absolute; inset: 0; overflow: hidden; pointer-events: none; z-index: 0; }
                .cloud {
                    position: absolute;
                    border-radius: 50%;
                    background: radial-gradient(circle at 50% 50%,
                        rgba(0, 217, 255, 0.42) 0%,
                        rgba(0, 184, 217, 0.13) 48%,
                        rgba(0, 184, 217, 0) 75%);
                    filter: blur(32px);
                    /* Drifting soft glow (blur + the cloudDrift animation below). Heavy
                       for weak/old GPUs, but the choppiness we chased was a machine-state
                       issue, not this effect (see reference_web_prod_build_ice).
                       NOTE: keep in sync with resources/index.html .cloud (duplicated). */
                    will-change: transform;
                }
                .cloud.c1 { width: 540px; height: 540px; top: -100px; left: -28%; animation: cloudDrift 43s linear infinite; opacity: .7; }
                .cloud.c2 { width: 380px; height: 380px; top: 21%;  left: -21%; animation: cloudDrift 30s linear infinite -9s;  opacity: .55; }
                .cloud.c3 { width: 680px; height: 680px; top: -18%; left: -33%; animation: cloudDriftUp 58s linear infinite -22s; opacity: .45; }
                .cloud.c4 { width: 300px; height: 300px; top: 56%;  left: -21%; animation: cloudDriftDown 25s linear infinite -15s; opacity: .65; }
                .cloud.c5 { width: 450px; height: 450px; top: 31%;  left: -26%; animation: cloudDrift 49s linear infinite -33s; opacity: .5; }
                @keyframes cloudDrift     { from { transform: translate(0, 0); }     to { transform: translate(130vw, 0); } }
                @keyframes cloudDriftUp   { from { transform: translate(0, 0); }     to { transform: translate(130vw, -20px); } }
                @keyframes cloudDriftDown { from { transform: translate(0, 0); }     to { transform: translate(130vw, 15px); } }
                @media (prefers-reduced-motion: reduce) { .cloud { animation: none; } }
            </style>
            <div class="hero-wrap">
                <div class="cloud-backdrop" aria-hidden="true">
                    <div class="cloud c1"></div>
                    <div class="cloud c2"></div>
                    <div class="cloud c3"></div>
                    <div class="cloud c4"></div>
                    <div class="cloud c5"></div>
                </div>
            <div style="padding: ${if (isMobile) "40px 24px 60px" else "80px 48px 100px"}; text-align: center; max-width: 900px; margin: 0 auto; position: relative; z-index: 1;">
                <!-- Animated percentage display -->
                <div id="hero-pct-container" style="font-size: ${if (isMobile) "72px" else "120px"}; font-weight: 800; color: #888; margin-bottom: 0; text-shadow: none; transition: color 0.15s, text-shadow 0.15s;">
                    <span id="hero-percentage">0</span>%
                </div>
                <div id="hero-progress-bar" style="width: ${if (isMobile) "100px" else "160px"}; height: ${if (isMobile) "3px" else "4px"}; margin: ${if (isMobile) "6px" else "10px"} auto 0; background: #888; border-radius: 2px; overflow: hidden;">
                    <div id="hero-progress-fill" style="width: 0%; height: 100%; border-radius: 2px; background: #888;"></div>
                </div>
                <div style="font-size: ${if (isMobile) "12px" else "16px"}; color: #666; margin-top: ${if (isMobile) "12px" else "16px"}; margin-bottom: ${if (isMobile) "24px" else "48px"}; letter-spacing: 2px; text-transform: uppercase;">${StringKey.LANDING_SAVINGS_RATE.tr()}</div>

                <!-- Headline -->
                <h1 style="font-size: ${if (isMobile) "32px" else "56px"}; font-weight: 700; margin-bottom: ${if (isMobile) "16px" else "24px"}; line-height: 1.1;">
                    ${StringKey.LANDING_ONE_NUMBER.tr()}<br><span style="color: #00d9ff;">${StringKey.LANDING_INFINITE_POSSIBILITIES.tr()}</span>
                </h1>

                <!-- Subheadline -->
                <p style="font-size: ${if (isMobile) "16px" else "20px"}; color: #99a0a6; max-width: 700px; margin: 0 auto 16px; line-height: 1.6;">
                    ${StringKey.LANDING_HERO_SUBTITLE.tr()}
                </p>
                <p style="font-size: ${if (isMobile) "14px" else "16px"}; color: #666; margin-bottom: ${if (isMobile) "24px" else "48px"};">
                    ${StringKey.LANDING_HERO_SUBTITLE_SHORT.tr()}
                </p>

                <!-- CTA Buttons -->
                <div style="display: flex; gap: ${if (isMobile) "12px" else "16px"}; justify-content: center; margin-bottom: ${if (isMobile) "24px" else "48px"}; ${if (isMobile) "flex-direction: column; align-items: center;" else ""}">
                    <span id="hero-cta-primary" style="background: #00d9ff; color: #0a0a0a; padding: ${if (isMobile) "12px 24px" else "16px 32px"}; border-radius: 8px; font-weight: 600; font-size: ${if (isMobile) "16px" else "18px"}; cursor: pointer; transition: all 0.2s; ${if (isMobile) "width: 100%; max-width: 280px;" else ""}">${StringKey.LANDING_DISCOVER_YOUR_NUMBER.tr()}</span>
                    <span id="hero-cta-secondary" style="background: transparent; color: #ffffff; padding: ${if (isMobile) "12px 24px" else "16px 32px"}; border-radius: 8px; font-weight: 600; font-size: ${if (isMobile) "16px" else "18px"}; cursor: pointer; border: 2px solid #333; transition: all 0.2s; ${if (isMobile) "width: 100%; max-width: 280px;" else ""}">${StringKey.LANDING_SEE_HOW_IT_WORKS.tr()}</span>
                </div>

                <!-- Trust badges -->
                <div style="display: flex; gap: ${if (isMobile) "16px" else "32px"}; justify-content: center; flex-wrap: wrap;">
                    <div style="display: flex; align-items: center; gap: 8px; color: #888; font-size: ${if (isMobile) "12px" else "14px"};">
                        <span style="color: #00d9ff;">🔒</span><span>${StringKey.LANDING_BANK_LEVEL_SECURITY.tr()}</span>
                    </div>
                    <div style="display: flex; align-items: center; gap: 8px; color: #888; font-size: ${if (isMobile) "12px" else "14px"};">
                        <span style="color: #00d9ff;">🏦</span><span>${StringKey.LANDING_INSTITUTIONS_COUNT.tr()}</span>
                    </div>
                    <div style="display: flex; align-items: center; gap: 8px; color: #888; font-size: ${if (isMobile) "12px" else "14px"};">
                        <span style="color: #00d9ff;">✓</span><span>${StringKey.LANDING_NO_CREDIT_CARD.tr()}</span>
                    </div>
                </div>
            </div>
            </div><!-- /hero-wrap -->

            <!-- Philosophy Section (hidden on mobile) -->
            ${if (!isMobile) """
            <div style="padding: 100px 48px; background: #0f0f0f;">
                <div style="max-width: 1200px; margin: 0 auto;">
                    <h2 style="font-size: 40px; text-align: center; margin-bottom: 24px;">${StringKey.LANDING_WHY_ONE_NUMBER.tr()}</h2>
                    <p style="font-size: 18px; color: #99a0a6; text-align: center; max-width: 800px; margin: 0 auto 64px;">
                        ${StringKey.LANDING_WHY_ONE_NUMBER_DESC.tr()}
                    </p>

                    <div id="philosophy-grid" style="display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 32px;">
                        <!-- Column 1 -->
                        <div class="philosophy-card philo-left">
                            <div class="philo-icon" style="width: 56px; height: 56px; background: rgba(0, 217, 255, 0.1); border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 26px; font-weight: 300; color: #00d9ff; margin-bottom: 24px;">∑</div>
                            <h3 style="font-size: 22px; margin-bottom: 16px;">${StringKey.LANDING_SIMPLE_MATH.tr()}</h3>
                            <div style="color: #99a0a6; line-height: 1.7;">
                                <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 16px; flex-wrap: nowrap;">
                                    <span style="white-space: nowrap;">${StringKey.FORMULA_YOUR_SAVINGS_PERCENT.tr()}</span>
                                    <span style="font-size: 20px;">=</span>
                                    <div style="display: flex; flex-direction: column; align-items: center;">
                                        <span style="white-space: nowrap; padding: 0 12px;">${StringKey.FORMULA_INCOME_MINUS_EXPENSES.tr()}</span>
                                        <div style="width: 100%; height: 2px; background: #666; margin: 6px 0;"></div>
                                        <span style="padding: 0 12px;">${StringKey.DASHBOARD_INCOME.tr()}</span>
                                    </div>
                                </div>
                                <p style="margin: 0;">${StringKey.LANDING_SIMPLE_MATH_DESC.tr()}</p>
                            </div>
                        </div>
                        <!-- Column 2 -->
                        <div class="philosophy-card philo-middle">
                            <div class="philo-icon" style="width: 56px; height: 56px; background: rgba(51, 204, 51, 0.1); border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 26px; font-weight: 300; color: #33cc33; margin-bottom: 24px;">◎</div>
                            <h3 style="font-size: 22px; margin-bottom: 16px;">${StringKey.LANDING_PSYCHOLOGY_TITLE.tr()}</h3>
                            <p style="color: #99a0a6; line-height: 1.7;">${StringKey.LANDING_PSYCHOLOGY_DESC.tr()}</p>
                            <div style="margin-top: 24px; padding: 16px; background: rgba(51, 204, 51, 0.15); border-radius: 8px; color: #33cc33; font-weight: 600; font-size: 14px;">${StringKey.LANDING_PSYCHOLOGY_STAT.tr()}</div>
                        </div>
                        <!-- Column 3 -->
                        <div class="philosophy-card philo-right">
                            <div class="philo-icon" style="width: 56px; height: 56px; background: rgba(255, 166, 0, 0.1); border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 26px; font-weight: 300; color: #ffa600; margin-bottom: 24px;">△</div>
                            <h3 style="font-size: 22px; margin-bottom: 16px;">${StringKey.LANDING_SMALL_CHANGES.tr()}</h3>
                            <p style="color: #99a0a6; line-height: 1.7;">${StringKey.LANDING_SMALL_CHANGES_DESC.tr()}</p>
                        </div>
                    </div>
                </div>
            </div>
            """ else ""}

            <!-- See It In Action — device showcase (web / iOS / Android) -->
            <div id="showcase" style="padding: ${if (isMobile) "60px 20px" else "100px 48px"}; background: #0c0c0e;">
                <div style="max-width: 1080px; margin: 0 auto; text-align: center;">
                    <h2 style="font-size: ${if (isMobile) "28px" else "40px"}; margin: 0 0 12px;">${if (i18n.LocaleManager.getCurrentLocale().code == "fr") "Voyez-le en action" else "See it in action"}</h2>
                    <p style="color: #99a0a6; font-size: ${if (isMobile) "15px" else "18px"}; margin: 0 auto 36px; max-width: 560px; line-height: 1.6;">${if (i18n.LocaleManager.getCurrentLocale().code == "fr") "Regardez l'application faire le travail — choisissez votre plateforme." else "Watch the app do the work — pick your platform."}</p>
                    <div id="showcase-tabs" style="display: inline-flex; gap: 4px; padding: 5px; background: #161619; border: 1px solid #2a2a2e; border-radius: 999px; margin-bottom: ${if (isMobile) "32px" else "44px"};">
                        <button class="showcase-tab" data-sc="web" style="border: none; cursor: pointer; padding: 8px 20px; border-radius: 999px; font-size: 14px; font-weight: 600; background: #00d9ff; color: #0a0a0a; transition: all 0.2s;">Web</button>
                        <button class="showcase-tab" data-sc="ios" style="border: none; cursor: pointer; padding: 8px 20px; border-radius: 999px; font-size: 14px; font-weight: 600; background: transparent; color: #99a0a6; transition: all 0.2s;">iOS</button>
                        <button class="showcase-tab" data-sc="android" style="border: none; cursor: pointer; padding: 8px 20px; border-radius: 999px; font-size: 14px; font-weight: 600; background: transparent; color: #99a0a6; transition: all 0.2s;">Android</button>
                    </div>
                    <div style="display: flex; justify-content: center; align-items: center; min-height: ${if (isMobile) "440px" else "560px"};">
                        <div class="showcase-frame" data-sc="web" style="display: block; width: 100%; max-width: 760px;">
                            <div style="background: #1c1c20; border: 1px solid #2f2f35; border-radius: 14px; overflow: hidden; box-shadow: 0 30px 80px rgba(0,0,0,0.5);">
                                <div style="display: flex; align-items: center; gap: 6px; padding: 10px 14px; background: #232329; border-bottom: 1px solid #2f2f35;">
                                    <span style="width: 11px; height: 11px; border-radius: 50%; background: #ff5f57;"></span>
                                    <span style="width: 11px; height: 11px; border-radius: 50%; background: #febc2e;"></span>
                                    <span style="width: 11px; height: 11px; border-radius: 50%; background: #28c840;"></span>
                                    <span style="margin: 0 auto; padding: 3px 44px; background: #161619; border-radius: 6px; color: #6b7280; font-size: 12px;">wiz3r.com</span>
                                </div>
                                ${scMedia("web", "62.5%")}
                            </div>
                        </div>
                        <div class="showcase-frame" data-sc="ios" style="display: none;">${scPhone("ios")}</div>
                        <div class="showcase-frame" data-sc="android" style="display: none;">${scPhone("android")}</div>
                    </div>
                    <div style="margin-top: ${if (isMobile) "32px" else "44px"};">
                        <span id="showcase-cta" style="display: inline-block; background: #00d9ff; color: #0a0a0a; padding: ${if (isMobile) "14px 32px" else "16px 40px"}; border-radius: 8px; font-weight: 600; font-size: ${if (isMobile) "16px" else "18px"}; cursor: pointer; transition: all 0.2s;">${StringKey.LANDING_GET_STARTED.tr()}</span>
                    </div>
                </div>
            </div>

            <!-- Features Section (hidden on mobile) -->
            ${if (!isMobile) """
            <div id="features" style="padding: 100px 48px;">
                <div style="max-width: 1200px; margin: 0 auto;">
                    <h2 style="font-size: 40px; text-align: center; margin-bottom: 64px;">${StringKey.LANDING_EVERYTHING_YOU_NEED.tr()}</h2>

                    <div id="features-grid" style="display: grid; grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); gap: 32px;">
                        <!-- Feature 1 -->
                        <div class="feature-card">
                            <div style="display: flex; align-items: center; gap: 16px; margin-bottom: 20px;">
                                <div class="feature-icon" style="width: 48px; height: 48px; background: rgba(0, 217, 255, 0.1); border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 22px; font-weight: 300; color: #00d9ff;">◉</div>
                                <span style="background: rgba(0, 217, 255, 0.15); color: #00d9ff; padding: 6px 12px; border-radius: 20px; font-size: 12px; font-weight: 600;">${StringKey.LANDING_UPDATED_REALTIME.tr()}</span>
                            </div>
                            <h3 style="font-size: 22px; margin-bottom: 12px;">${StringKey.LANDING_SEE_REAL_NUMBER.tr()}</h3>
                            <p style="color: #99a0a6; line-height: 1.6;">${StringKey.LANDING_SEE_REAL_NUMBER_DESC.tr()}</p>
                        </div>
                        <!-- Feature 2 -->
                        <div class="feature-card">
                            <div style="display: flex; align-items: center; gap: 16px; margin-bottom: 20px;">
                                <div class="feature-icon" style="width: 48px; height: 48px; background: rgba(51, 204, 51, 0.1); border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 22px; font-weight: 300; color: #33cc33;">⊕</div>
                                <span style="background: rgba(51, 204, 51, 0.15); color: #33cc33; padding: 6px 12px; border-radius: 20px; font-size: 12px; font-weight: 600;">${StringKey.LANDING_AI_POWERED.tr()}</span>
                            </div>
                            <h3 style="font-size: 22px; margin-bottom: 12px;">${StringKey.LANDING_UNDERSTAND_WHAT_MOVES.tr()}</h3>
                            <p style="color: #99a0a6; line-height: 1.6;">${StringKey.LANDING_UNDERSTAND_DESC.tr()}</p>
                        </div>
                        <!-- Feature 3 -->
                        <div class="feature-card">
                            <div style="display: flex; align-items: center; gap: 16px; margin-bottom: 20px;">
                                <div class="feature-icon" style="width: 48px; height: 48px; background: rgba(255, 166, 0, 0.1); border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 22px; font-weight: 300; color: #ffa600;">▲</div>
                                <span style="background: rgba(255, 166, 0, 0.15); color: #ffa600; padding: 6px 12px; border-radius: 20px; font-size: 12px; font-weight: 600;">${StringKey.LANDING_PERSONALIZED.tr()}</span>
                            </div>
                            <h3 style="font-size: 22px; margin-bottom: 12px;">${StringKey.LANDING_IMPROVE_AUTOMATICALLY.tr()}</h3>
                            <p style="color: #99a0a6; line-height: 1.6;">${StringKey.LANDING_IMPROVE_DESC.tr()}</p>
                        </div>
                    </div>
                </div>
            </div>
            """ else ""}

            <!-- Advice Cards Section -->
            <div style="padding: ${if (isMobile) "60px 20px" else "100px 48px"}; background: #0f0f0f;">
                <div style="max-width: 1200px; margin: 0 auto;">
                    <h2 style="font-size: ${if (isMobile) "28px" else "40px"}; text-align: center; margin-bottom: ${if (isMobile) "16px" else "24px"};">${StringKey.LANDING_BUILDING_HABITS.tr()}</h2>
                    <p style="font-size: ${if (isMobile) "14px" else "18px"}; color: #99a0a6; text-align: center; max-width: 700px; margin: 0 auto ${if (isMobile) "32px" else "64px"};">
                        ${StringKey.LANDING_BUILDING_HABITS_DESC.tr()}
                    </p>

                    <div id="advice-ribbon" class="advice-ribbon" style="display: flex; gap: 24px;">
                        <!-- Card 1 -->
                        <div class="advice-card" style="min-width: 300px; background: #141414; border-radius: 16px; padding: 24px; border: 1px solid #1f1f1f; flex-shrink: 0;">
                            <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 16px;">
                                <span style="font-size: 20px; color: #ffa600;">◆</span>
                                <span style="font-weight: 600; font-size: 16px;">${StringKey.ADVICE_DINING_ALERT.tr()}</span>
                            </div>
                            <p style="color: #99a0a6; font-size: 14px; line-height: 1.6; margin-bottom: 20px;">
                                ${StringKey.ADVICE_DINING_SPENT.tr().replaceFirst("%s", "${'$'}340").replaceFirst("%s", "12%")}<br><br>${StringKey.ADVICE_DINING_BOOST.tr().replaceFirst("%s", "6%")}
                            </p>
                            <span style="color: #00d9ff; font-size: 14px; cursor: pointer;">${StringKey.ADVICE_SEE_DINING.tr()} →</span>
                        </div>
                        <!-- Card 2 -->
                        <div class="advice-card" style="min-width: 300px; background: #141414; border-radius: 16px; padding: 24px; border: 1px solid #1f1f1f; flex-shrink: 0;">
                            <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 16px;">
                                <span style="font-size: 20px; color: #33cc33;">★</span>
                                <span style="font-weight: 600; font-size: 16px;">${StringKey.ADVICE_NEW_PERSONAL_BEST.tr()}</span>
                            </div>
                            <p style="color: #99a0a6; font-size: 14px; line-height: 1.6; margin-bottom: 20px;">
                                ${StringKey.ADVICE_PERSONAL_BEST_MESSAGE.tr().replaceFirst("%s", "24%").replaceFirst("%s", "19%")}<br><br>${StringKey.ADVICE_KEEP_IT_UP.tr().replaceFirst("%s", "${'$'}2,400")}
                            </p>
                            <span style="color: #00d9ff; font-size: 14px; cursor: pointer;">${StringKey.ADVICE_SHARE_WIN.tr()} →</span>
                        </div>
                        <!-- Card 3 -->
                        <div class="advice-card" style="min-width: 300px; background: #141414; border-radius: 16px; padding: 24px; border: 1px solid #1f1f1f; flex-shrink: 0;">
                            <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 16px;">
                                <span style="font-size: 20px; color: #00d9ff;">◇</span>
                                <span style="font-weight: 600; font-size: 16px;">${StringKey.ADVICE_WEEKEND_TIP.tr()}</span>
                            </div>
                            <p style="color: #99a0a6; font-size: 14px; line-height: 1.6; margin-bottom: 20px;">
                                ${StringKey.ADVICE_WEEKEND_ACCOUNT.tr().replaceFirst("%s", "45%")}<br><br>${StringKey.ADVICE_WEEKEND_ACTIVITY.tr()} ~${'$'}80.
                            </p>
                            <span style="color: #00d9ff; font-size: 14px; cursor: pointer;">${StringKey.ADVICE_BROWSE_EVENTS.tr()} →</span>
                        </div>
                        <!-- Card 4 -->
                        <div class="advice-card" style="min-width: 300px; background: #141414; border-radius: 16px; padding: 24px; border: 1px solid #1f1f1f; flex-shrink: 0;">
                            <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 16px;">
                                <span style="font-size: 20px; color: #00d9ff;">◎</span>
                                <span style="font-weight: 600; font-size: 16px;">${StringKey.ADVICE_VACATION_FUND.tr()}</span>
                            </div>
                            <p style="color: #99a0a6; font-size: 14px; line-height: 1.6; margin-bottom: 20px;">
                                ${StringKey.ADVICE_VACATION_MESSAGE.tr().replaceFirst("%s", "22%").replaceFirst("%s", "${'$'}3,000").replaceFirst("%s", "4 months")}<br><br>${StringKey.ADVICE_VACATION_BOOST.tr().replaceFirst("%s", "25%").replaceFirst("%s", "3 months")}
                            </p>
                            <span style="color: #00d9ff; font-size: 14px; cursor: pointer;">${StringKey.ADVICE_ADJUST_ALLOCATION.tr()} →</span>
                        </div>
                    </div>

                    <p style="font-size: 16px; color: #666; text-align: center; margin-top: 32px;">
                        ${StringKey.LANDING_ADVICE_FOOTER.tr()}
                    </p>
                </div>
            </div>

            <!-- Pricing Section -->
            <div id="pricing" style="padding: ${if (isMobile) "60px 20px" else "100px 48px"};">
                <div style="max-width: 840px; margin: 0 auto;">
                    <h2 style="font-size: ${if (isMobile) "28px" else "40px"}; text-align: center; margin-bottom: 16px;">${StringKey.LANDING_PRICING_TITLE.tr()}</h2>
                    <p style="font-size: ${if (isMobile) "14px" else "18px"}; color: #99a0a6; text-align: center; margin-bottom: ${if (isMobile) "24px" else "40px"};">
                        ${StringKey.LANDING_PRICING_SUBTITLE.tr()}
                    </p>

                    <!-- Billing toggle -->
                    <div style="display: flex; justify-content: center; margin-bottom: ${if (isMobile) "24px" else "40px"};">
                        <div style="display: flex; background: #141414; border-radius: 12px; padding: 4px; border: 1px solid #1f1f1f;">
                            <div id="toggle-monthly" style="padding: 10px 24px; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer; background: #00d9ff; color: #0a0a0a; transition: all 0.2s;">${StringKey.PRICING_MONTHLY.tr()}</div>
                            <div id="toggle-yearly" style="padding: 10px 24px; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer; background: transparent; color: #99a0a6; transition: all 0.2s; display: flex; align-items: center; gap: 8px;">${StringKey.PRICING_YEARLY.tr()} <span style="background: rgba(51, 204, 51, 0.15); color: #33cc33; padding: 2px 8px; border-radius: 10px; font-size: 11px;">${StringKey.PRICING_SAVE_14.tr()}</span></div>
                        </div>
                    </div>

                    <!-- Two-tier pricing cards (3D flip containers) -->
                    <div id="pricing-grid" style="display: flex; gap: 24px; flex-wrap: wrap; justify-content: center; perspective: 1800px;">
                        <!-- Standard Card (flip container) -->
                        <div class="pricing-card pricing-standard pricing-flipper" style="flex: 1; min-width: 280px; max-width: 380px;">
                            <div class="pricing-flip-inner">
                                <!-- FRONT (monthly) -->
                                <div class="pricing-face pricing-face-front" style="background: #141414; border: 1px solid #333; border-radius: 16px; padding: 32px 24px; text-align: center;">
                                    <h3 style="font-size: ${if (isMobile) "22px" else "28px"}; margin-bottom: 8px;">${StringKey.TIER_STANDARD.tr()}</h3>
                                    <p style="color: #888; font-size: 14px; margin-bottom: 24px;">${StringKey.TIER_STANDARD_SUBTITLE.tr()}</p>
                                    <div style="margin-bottom: 32px;">
                                        <span style="font-size: ${if (isMobile) "40px" else "48px"}; font-weight: 700;">${'$'}6.99</span>
                                        <span style="color: #666; font-size: 18px;">/month</span>
                                    </div>
                                    <ul style="list-style: none; padding: 0; margin: 0 0 32px 0; text-align: left;">
                                        <li style="padding: 8px 0; color: #e0e0e0; font-size: 14px; display: flex; align-items: center; gap: 10px;"><span style="color: #33cc33;">✓</span>${StringKey.PRICING_UNLIMITED_ACCOUNTS.tr()}</li>
                                        <li style="padding: 8px 0; color: #e0e0e0; font-size: 14px; display: flex; align-items: center; gap: 10px;"><span style="color: #33cc33;">✓</span>${StringKey.PRICING_REALTIME_TRACKING.tr()}</li>
                                        <li style="padding: 8px 0; color: #e0e0e0; font-size: 14px; display: flex; align-items: center; gap: 10px;"><span style="color: #33cc33;">✓</span>${StringKey.PRICING_AI_ADVICE_CARDS.tr()}</li>
                                        <li style="padding: 8px 0; color: #e0e0e0; font-size: 14px; display: flex; align-items: center; gap: 10px;"><span style="color: #33cc33;">✓</span>${StringKey.PRICING_UNLIMITED_GOALS.tr()}</li>
                                        <li style="padding: 8px 0; color: #e0e0e0; font-size: 14px; display: flex; align-items: center; gap: 10px;"><span style="color: #33cc33;">✓</span>${StringKey.PRICING_GOAL_OPTIMIZATION.tr()}</li>
                                        <li style="padding: 8px 0; color: #e0e0e0; font-size: 14px; display: flex; align-items: center; gap: 10px;"><span style="color: #33cc33;">✓</span>${StringKey.PRICING_CROSS_PLATFORM.tr()}</li>
                                        <li style="padding: 8px 0; color: #e0e0e0; font-size: 14px; display: flex; align-items: center; gap: 10px;"><span style="color: #33cc33;">✓</span>${StringKey.PRICING_CATEGORY_CUSTOMIZATION.tr()}</li>
                                    </ul>
                                    <div id="standard-cta" style="border: 2px solid #555; color: #e0e0e0; padding: 14px 32px; border-radius: 8px; text-align: center; font-weight: 700; font-size: ${if (isMobile) "15px" else "16px"}; cursor: pointer; transition: all 0.2s; margin-bottom: 12px;">${StringKey.PRICING_START_FREE_TRIAL.tr()}</div>
                                    <p style="color: #666; font-size: 13px; margin: 0;">${StringKey.PRICING_CANCEL_ANYTIME.tr()}</p>
                                </div>
                                <!-- BACK (yearly) -->
                                <div class="pricing-face pricing-face-back" style="background: #141414; border: 1px solid #333; border-radius: 16px; padding: 32px 24px; text-align: center;">
                                    <h3 style="font-size: ${if (isMobile) "22px" else "28px"}; margin-bottom: 8px;">${StringKey.TIER_STANDARD.tr()}</h3>
                                    <p style="color: #888; font-size: 14px; margin-bottom: 24px;">${StringKey.TIER_STANDARD_SUBTITLE.tr()}</p>
                                    <div style="margin-bottom: 32px;">
                                        <span style="font-size: ${if (isMobile) "40px" else "48px"}; font-weight: 700;">${'$'}72</span>
                                        <span style="color: #666; font-size: 18px;">/year</span>
                                        <div style="color: #33cc33; font-size: 14px; margin-top: 4px;">${'$'}6/mo — ${StringKey.PRICING_SAVE_14.tr()}</div>
                                    </div>
                                    <ul style="list-style: none; padding: 0; margin: 0 0 32px 0; text-align: left;">
                                        <li style="padding: 8px 0; color: #e0e0e0; font-size: 14px; display: flex; align-items: center; gap: 10px;"><span style="color: #33cc33;">✓</span>${StringKey.PRICING_UNLIMITED_ACCOUNTS.tr()}</li>
                                        <li style="padding: 8px 0; color: #e0e0e0; font-size: 14px; display: flex; align-items: center; gap: 10px;"><span style="color: #33cc33;">✓</span>${StringKey.PRICING_REALTIME_TRACKING.tr()}</li>
                                        <li style="padding: 8px 0; color: #e0e0e0; font-size: 14px; display: flex; align-items: center; gap: 10px;"><span style="color: #33cc33;">✓</span>${StringKey.PRICING_AI_ADVICE_CARDS.tr()}</li>
                                        <li style="padding: 8px 0; color: #e0e0e0; font-size: 14px; display: flex; align-items: center; gap: 10px;"><span style="color: #33cc33;">✓</span>${StringKey.PRICING_UNLIMITED_GOALS.tr()}</li>
                                        <li style="padding: 8px 0; color: #e0e0e0; font-size: 14px; display: flex; align-items: center; gap: 10px;"><span style="color: #33cc33;">✓</span>${StringKey.PRICING_GOAL_OPTIMIZATION.tr()}</li>
                                        <li style="padding: 8px 0; color: #e0e0e0; font-size: 14px; display: flex; align-items: center; gap: 10px;"><span style="color: #33cc33;">✓</span>${StringKey.PRICING_CROSS_PLATFORM.tr()}</li>
                                        <li style="padding: 8px 0; color: #e0e0e0; font-size: 14px; display: flex; align-items: center; gap: 10px;"><span style="color: #33cc33;">✓</span>${StringKey.PRICING_CATEGORY_CUSTOMIZATION.tr()}</li>
                                    </ul>
                                    <div id="standard-cta-back" class="standard-cta-back" style="border: 2px solid #555; color: #e0e0e0; padding: 14px 32px; border-radius: 8px; text-align: center; font-weight: 700; font-size: ${if (isMobile) "15px" else "16px"}; cursor: pointer; transition: all 0.2s; margin-bottom: 12px;">${StringKey.PRICING_START_FREE_TRIAL.tr()}</div>
                                    <p style="color: #666; font-size: 13px; margin: 0;">${StringKey.PRICING_CANCEL_ANYTIME.tr()}</p>
                                </div>
                            </div>
                        </div>

                        <!-- Pro Card (highlighted, flip container) -->
                        <div class="pricing-card pricing-pro pricing-flipper" style="flex: 1; min-width: 280px; max-width: 380px;">
                            <div class="pricing-flip-inner">
                                <!-- FRONT (monthly) -->
                                <div class="pricing-face pricing-face-front" style="background: #141414; border: 2px solid #00d9ff; border-radius: 16px; padding: 32px 24px; box-shadow: 0 0 30px rgba(0,217,255,0.15); position: relative; text-align: center; overflow: hidden;">
                                    <div class="pro-shimmer" aria-hidden="true"></div>
                                    <div class="pro-best-value">${StringKey.PRICING_BEST_VALUE.tr().uppercase()}</div>
                                    <h3 style="font-size: ${if (isMobile) "22px" else "28px"}; margin-bottom: 8px; color: #00d9ff;">${StringKey.TIER_PRO.tr()}</h3>
                                    <p style="color: #888; font-size: 14px; margin-bottom: 24px;">${StringKey.TIER_PRO_SUBTITLE.tr()}</p>
                                    <div style="margin-bottom: 32px;">
                                        <span style="font-size: ${if (isMobile) "40px" else "48px"}; font-weight: 700;">${'$'}12.99</span>
                                        <span style="color: #666; font-size: 18px;">/month</span>
                                    </div>
                                    <ul style="list-style: none; padding: 0; margin: 0 0 32px 0; text-align: left;">
                                        <li style="padding: 8px 0; color: #e0e0e0; font-size: 14px; display: flex; align-items: center; gap: 10px;"><span style="color: #00d9ff;">✓</span>${StringKey.PRICING_EVERYTHING_IN_STANDARD.tr()}</li>
                                        <li style="padding: 8px 0; color: #e0e0e0; font-size: 14px; display: flex; align-items: center; gap: 10px;"><span style="color: #00d9ff;">✓</span>${StringKey.PRICING_MULTI_SELECT_AND_BATCH.tr()}</li>
                                        <li style="padding: 8px 0; color: #e0e0e0; font-size: 14px; display: flex; align-items: center; gap: 10px;"><span style="color: #00d9ff;">✓</span>${StringKey.PRICING_TRANSACTION_NOTIFICATIONS.tr()}</li>
                                        <li style="padding: 8px 0; color: #e0e0e0; font-size: 14px; display: flex; align-items: center; gap: 10px;"><span style="color: #00d9ff;">✓</span>${StringKey.PRICING_SPLIT_TRANSACTIONS.tr()}</li>
                                        <li style="padding: 8px 0; color: #e0e0e0; font-size: 14px; display: flex; align-items: center; gap: 10px;"><span style="color: #00d9ff;">✓</span>${StringKey.PRICING_DISTRIBUTE_TRANSACTIONS.tr()}</li>
                                        <li style="padding: 8px 0; color: #e0e0e0; font-size: 14px; display: flex; align-items: center; gap: 10px;"><span style="color: #00d9ff;">✓</span>${StringKey.PRICING_CUSTOMIZABLE_DASHBOARD.tr()}</li>
                                        <li style="padding: 8px 0; color: #e0e0e0; font-size: 14px; display: flex; align-items: center; gap: 10px;"><span style="color: #00d9ff;">✓</span>${StringKey.PRICING_CONTEXT_FILTERS.tr()}</li>
                                    </ul>
                                    <div id="pro-cta" style="background: #00d9ff; color: #0a0a0a; padding: 14px 32px; border-radius: 8px; text-align: center; font-weight: 700; font-size: ${if (isMobile) "15px" else "16px"}; cursor: pointer; transition: all 0.2s; margin-bottom: 12px;">${StringKey.PRICING_START_FREE_TRIAL.tr()}</div>
                                    <p style="color: #666; font-size: 13px; margin: 0;">${StringKey.PRICING_CANCEL_ANYTIME.tr()}</p>
                                </div>
                                <!-- BACK (yearly) -->
                                <div class="pricing-face pricing-face-back" style="background: #141414; border: 2px solid #00d9ff; border-radius: 16px; padding: 32px 24px; box-shadow: 0 0 30px rgba(0,217,255,0.15); position: relative; text-align: center; overflow: hidden;">
                                    <div class="pro-best-value">${StringKey.PRICING_BEST_VALUE.tr().uppercase()}</div>
                                    <h3 style="font-size: ${if (isMobile) "22px" else "28px"}; margin-bottom: 8px; color: #00d9ff;">${StringKey.TIER_PRO.tr()}</h3>
                                    <p style="color: #888; font-size: 14px; margin-bottom: 24px;">${StringKey.TIER_PRO_SUBTITLE.tr()}</p>
                                    <div style="margin-bottom: 32px;">
                                        <span style="font-size: ${if (isMobile) "40px" else "48px"}; font-weight: 700;">${'$'}132</span>
                                        <span style="color: #666; font-size: 18px;">/year</span>
                                        <div style="color: #33cc33; font-size: 14px; margin-top: 4px;">${'$'}11/mo — ${StringKey.PRICING_SAVE_15.tr()}</div>
                                    </div>
                                    <ul style="list-style: none; padding: 0; margin: 0 0 32px 0; text-align: left;">
                                        <li style="padding: 8px 0; color: #e0e0e0; font-size: 14px; display: flex; align-items: center; gap: 10px;"><span style="color: #00d9ff;">✓</span>${StringKey.PRICING_EVERYTHING_IN_STANDARD.tr()}</li>
                                        <li style="padding: 8px 0; color: #e0e0e0; font-size: 14px; display: flex; align-items: center; gap: 10px;"><span style="color: #00d9ff;">✓</span>${StringKey.PRICING_MULTI_SELECT_AND_BATCH.tr()}</li>
                                        <li style="padding: 8px 0; color: #e0e0e0; font-size: 14px; display: flex; align-items: center; gap: 10px;"><span style="color: #00d9ff;">✓</span>${StringKey.PRICING_TRANSACTION_NOTIFICATIONS.tr()}</li>
                                        <li style="padding: 8px 0; color: #e0e0e0; font-size: 14px; display: flex; align-items: center; gap: 10px;"><span style="color: #00d9ff;">✓</span>${StringKey.PRICING_SPLIT_TRANSACTIONS.tr()}</li>
                                        <li style="padding: 8px 0; color: #e0e0e0; font-size: 14px; display: flex; align-items: center; gap: 10px;"><span style="color: #00d9ff;">✓</span>${StringKey.PRICING_DISTRIBUTE_TRANSACTIONS.tr()}</li>
                                        <li style="padding: 8px 0; color: #e0e0e0; font-size: 14px; display: flex; align-items: center; gap: 10px;"><span style="color: #00d9ff;">✓</span>${StringKey.PRICING_CUSTOMIZABLE_DASHBOARD.tr()}</li>
                                        <li style="padding: 8px 0; color: #e0e0e0; font-size: 14px; display: flex; align-items: center; gap: 10px;"><span style="color: #00d9ff;">✓</span>${StringKey.PRICING_CONTEXT_FILTERS.tr()}</li>
                                    </ul>
                                    <div id="pro-cta-back" class="pro-cta-back" style="background: #00d9ff; color: #0a0a0a; padding: 14px 32px; border-radius: 8px; text-align: center; font-weight: 700; font-size: ${if (isMobile) "15px" else "16px"}; cursor: pointer; transition: all 0.2s; margin-bottom: 12px;">${StringKey.PRICING_START_FREE_TRIAL.tr()}</div>
                                    <p style="color: #666; font-size: 13px; margin: 0;">${StringKey.PRICING_CANCEL_ANYTIME.tr()}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Trust Section -->
            <div style="padding: ${if (isMobile) "48px 20px" else "80px 48px"}; background: #0f0f0f;">
                <div style="max-width: 900px; margin: 0 auto; text-align: center;">
                    <h2 style="font-size: ${if (isMobile) "24px" else "32px"}; margin-bottom: ${if (isMobile) "32px" else "48px"};">${StringKey.LANDING_DATA_SAFE.tr()}</h2>
                    <div id="trust-grid" style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 32px;">
                        <div class="trust-item">
                            <div style="display: flex; justify-content: center; margin-bottom: 12px;">
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#555" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                                    <circle cx="12" cy="16" r="1"></circle>
                                </svg>
                            </div>
                            <h4 style="font-size: 16px; margin-bottom: 8px;">${StringKey.SECURITY_BANK_ENCRYPTION.tr()}</h4>
                            <p style="color: #666; font-size: 14px; margin: 0;">${StringKey.SECURITY_256_BIT.tr()}</p>
                        </div>
                        <div class="trust-item">
                            <div style="display: flex; justify-content: center; margin-bottom: 12px;">
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="#555">
                                    <path d="M12 2L2 7v5c0 5.55 4.28 10.74 10 12 5.72-1.26 10-6.45 10-12V7l-10-5zm-1 4h2v2h-2V6zm-3 3h2v2H8V9zm6 0h2v2h-2V9zm-3 3h2v2h-2v-2zm-3 3h2v2H8v-2zm6 0h2v2h-2v-2z"/>
                                </svg>
                            </div>
                            <h4 style="font-size: 16px; margin-bottom: 8px;">${StringKey.SECURITY_PLAID_PARTNERSHIP.tr()}</h4>
                            <p style="color: #666; font-size: 14px; margin: 0;">${StringKey.SECURITY_TRUSTED_APPS.tr()}</p>
                        </div>
                        <div class="trust-item">
                            <div style="display: flex; justify-content: center; margin-bottom: 12px;">
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#555" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                    <polyline points="14 2 14 8 20 8"></polyline>
                                    <line x1="8" y1="13" x2="16" y2="13"></line>
                                    <line x1="8" y1="17" x2="16" y2="17"></line>
                                    <ellipse cx="12" cy="15" rx="3" ry="2" fill="none" stroke="#555" stroke-width="1.5"></ellipse>
                                    <circle cx="12" cy="15" r="0.5" fill="#555"></circle>
                                </svg>
                            </div>
                            <h4 style="font-size: 16px; margin-bottom: 8px;">${StringKey.SECURITY_READ_ONLY.tr()}</h4>
                            <p style="color: #666; font-size: 14px; margin: 0;">${StringKey.SECURITY_NO_MOVE_MONEY.tr()}</p>
                        </div>
                        <div class="trust-item">
                            <div style="display: flex; justify-content: center; margin-bottom: 12px;">
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#555" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line>
                                </svg>
                            </div>
                            <h4 style="font-size: 16px; margin-bottom: 8px;">${StringKey.SECURITY_NO_CREDENTIAL.tr()}</h4>
                            <p style="color: #666; font-size: 14px; margin: 0;">${StringKey.SECURITY_LOGIN_STAYS.tr()}</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Final CTA -->
            <div style="padding: ${if (isMobile) "60px 20px" else "100px 48px"}; background: linear-gradient(180deg, #0f0f0f 0%, #0a0a0a 100%); text-align: center;">
                <div id="mystery-pct" class="mystery-pct" style="font-size: ${if (isMobile) "48px" else "80px"}; font-weight: 800; color: #333; margin-bottom: ${if (isMobile) "16px" else "24px"};"><span class="mystery-char">?</span><span class="mystery-char">?</span><span class="mystery-char">%</span></div>
                <h2 style="font-size: ${if (isMobile) "28px" else "48px"}; margin-bottom: ${if (isMobile) "16px" else "24px"};">${StringKey.LANDING_WHATS_YOUR_NUMBER.tr()}</h2>
                <p style="font-size: ${if (isMobile) "14px" else "18px"}; color: #99a0a6; max-width: 600px; margin: 0 auto ${if (isMobile) "20px" else "32px"}; line-height: 1.6;">
                    ${StringKey.LANDING_WHATS_YOUR_NUMBER_DESC.tr()}
                </p>
                <p style="font-size: ${if (isMobile) "13px" else "16px"}; color: #666; margin-bottom: ${if (isMobile) "24px" else "40px"};">
                    ${StringKey.LANDING_WHATS_YOUR_NUMBER_SUBTEXT.tr()}
                </p>
                <div id="final-cta-btn" style="display: inline-block; background: #00d9ff; color: #0a0a0a; padding: ${if (isMobile) "14px 32px" else "18px 48px"}; border-radius: 8px; font-weight: 600; font-size: ${if (isMobile) "16px" else "18px"}; cursor: pointer; transition: all 0.2s;">
                    ${StringKey.LANDING_FIND_MY_SAVINGS_RATE.tr()}
                </div>
            </div>

            <!-- Footer -->
            <div style="padding: ${if (isMobile) "40px 20px 24px" else "64px 48px 32px"}; background: #050505; border-top: 1px solid #1a1a1a;">
                <div style="max-width: 1200px; margin: 0 auto;">
                    <!-- Logo and tagline -->
                    <div style="margin-bottom: ${if (isMobile) "32px" else "48px"}; ${if (isMobile) "text-align: center;" else ""}">
                        <img id="footer-logo" src="/wiz3r_logo.png?v=3" alt="wiz≡r" style="height: ${if (isMobile) "32px" else "40px"}; cursor: pointer; margin-bottom: 12px;">
                        <p style="color: #666; font-size: ${if (isMobile) "12px" else "14px"}; margin: 0;">${StringKey.APP_TAGLINE.tr()}</p>
                    </div>

                    <!-- Footer links -->
                    <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 32px; margin-bottom: 48px;">
                        <div>
                            <h4 style="color: #666; font-size: 12px; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 16px;">${StringKey.FOOTER_PRODUCT.tr()}</h4>
                            <p style="margin: 8px 0;"><a id="footer-features" href="#features" style="color: #99a0a6; text-decoration: none; font-size: 14px;">${StringKey.FOOTER_FEATURES.tr()}</a></p>
                            <p style="margin: 8px 0;"><a href="#pricing" style="color: #99a0a6; text-decoration: none; font-size: 14px;">${StringKey.FOOTER_PRICING.tr()}</a></p>
                        </div>
                        <div>
                            <h4 style="color: #666; font-size: 12px; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 16px;">${StringKey.FOOTER_COMPANY.tr()}</h4>
                            <p style="margin: 8px 0;"><a id="footer-guide" href="#" style="color: #99a0a6; text-decoration: none; font-size: 14px;">${StringKey.GUIDE_TITLE.tr()}</a></p>
                            <p style="margin: 8px 0;"><a id="footer-about" href="#" style="color: #99a0a6; text-decoration: none; font-size: 14px;">${StringKey.FOOTER_ABOUT.tr()}</a></p>
                            <p style="margin: 8px 0;"><a id="footer-contact" href="#" style="color: #99a0a6; text-decoration: none; font-size: 14px;">${StringKey.FOOTER_CONTACT.tr()}</a></p>
                            <p style="margin: 8px 0;"><a id="footer-team" href="#" style="color: #99a0a6; text-decoration: none; font-size: 14px;">${if (i18n.LocaleManager.getCurrentLocale().code == "fr") "Notre équipe" else "Our Team"}</a></p>
                            <p style="margin: 8px 0;"><a href="/blog" style="color: #99a0a6; text-decoration: none; font-size: 14px;">${if (i18n.LocaleManager.getCurrentLocale().code == "fr") "Blogue" else "Blog"}</a></p>
                        </div>
                        <div>
                            <h4 style="color: #666; font-size: 12px; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 16px;">${StringKey.FOOTER_LEGAL.tr()}</h4>
                            <p style="margin: 8px 0;"><a id="footer-terms" href="#" style="color: #99a0a6; text-decoration: none; font-size: 14px;">${StringKey.FOOTER_TERMS.tr()}</a></p>
                            <p style="margin: 8px 0;"><a id="footer-privacy" href="#" style="color: #99a0a6; text-decoration: none; font-size: 14px;">${StringKey.FOOTER_PRIVACY.tr()}</a></p>
                            <p style="margin: 8px 0;"><a id="footer-security" href="#" style="color: #99a0a6; text-decoration: none; font-size: 14px;">${StringKey.FOOTER_SECURITY.tr()}</a></p>
                        </div>
                        <div>
                            <h4 style="color: #666; font-size: 12px; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 16px;">${StringKey.FOOTER_CONNECT.tr()}</h4>
                            <p style="margin: 8px 0;"><a href="https://x.com/wiz3rapp" target="_blank" rel="noopener noreferrer" style="color: #99a0a6; text-decoration: none; font-size: 14px;">${StringKey.FOOTER_X.tr()}</a></p>
                            <p style="margin: 8px 0;"><a href="https://instagram.com/wiz3rapp" target="_blank" rel="noopener noreferrer" style="color: #99a0a6; text-decoration: none; font-size: 14px;">${StringKey.FOOTER_INSTAGRAM.tr()}</a></p>
                            <p style="margin: 8px 0;"><a href="https://linkedin.com/company/wiz3r" target="_blank" rel="noopener noreferrer" style="color: #99a0a6; text-decoration: none; font-size: 14px;">${StringKey.FOOTER_LINKEDIN.tr()}</a></p>
                            <p style="margin: 8px 0;"><a href="mailto:support@wiz3r.com" style="color: #99a0a6; text-decoration: none; font-size: 14px;">${StringKey.FOOTER_EMAIL.tr()}</a></p>
                        </div>
                    </div>

                    <!-- Copyright -->
                    <div style="border-top: 1px solid #1a1a1a; padding-top: 24px; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 16px;">
                        <p style="color: #666; font-size: 14px; margin: 0;">${StringKey.FOOTER_COPYRIGHT.tr()}</p>
                        <p style="color: #666; font-size: 14px; margin: 0;">${StringKey.FOOTER_MADE_WITH_LOVE.tr()}</p>
                    </div>
                </div>
            </div>
        """.trimIndent()
    }

    // Device-showcase media: a placeholder panel with a lazy <video> on top.
    // The loop files (/demo/{web,ios,android}.mp4|webm) may not exist yet — until
    // they do, the placeholder shows; attachEventListeners reveals a video only
    // once it actually loads. padPct sets the aspect ratio via padding-bottom.
    private fun scMedia(p: String, padPct: String): String {
        return """
            <div style="position: relative; width: 100%; padding-bottom: $padPct; background: #08080a;">
                <div class="sc-ph" style="position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 16px;">
                    <img src="/wiz3r_logo.png?v=3" alt="" style="height: 24px; opacity: 0.3;">
                    <div style="width: 52px; height: 52px; border-radius: 50%; background: rgba(0,217,255,0.12); border: 1px solid rgba(0,217,255,0.45); display: flex; align-items: center; justify-content: center;">
                        <span style="color: #00d9ff; font-size: 17px; margin-left: 3px;">&#9654;</span>
                    </div>
                </div>
                <video class="sc-vid" data-sc="$p" autoplay muted loop playsinline preload="none" style="position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; display: none; background: #08080a;">
                    <source src="/demo/$p.webm" type="video/webm">
                    <source src="/demo/$p.mp4" type="video/mp4">
                </video>
            </div>
        """
    }

    // A phone bezel (iOS notch / Android hole-punch) wrapping the showcase media.
    private fun scPhone(p: String): String {
        val notch = if (p == "ios")
            """<div style="position: absolute; top: 0; left: 50%; transform: translateX(-50%); width: 120px; height: 22px; background: #000; border-radius: 0 0 16px 16px; z-index: 2;"></div>"""
        else
            """<div style="position: absolute; top: 10px; left: 50%; transform: translateX(-50%); width: 9px; height: 9px; background: #000; border-radius: 50%; z-index: 2;"></div>"""
        return """
            <div style="position: relative; width: 258px; max-width: 78vw; padding: 10px; background: #1a1a1e; border: 1px solid #2f2f35; border-radius: 40px; box-shadow: 0 30px 80px rgba(0,0,0,0.5);">
                <div style="position: relative; border-radius: 30px; overflow: hidden;">
                    $notch
                    ${scMedia(p, "210%")}
                </div>
            </div>
        """
    }

    fun attachEventListeners(element: HTMLElement) {
        // Add click handlers after element is in DOM
        window.setTimeout({
            // Mobile menu handlers (for logged-in mobile users)
            if (isMobile && isLoggedIn) {
                // Hamburger menu open
                element.querySelector("#hamburgerMenu")?.addEventListener("click", {
                    (element.querySelector("#mobileMenuOverlay") as? HTMLElement)?.style?.display = "block"
                    (element.querySelector("#mobileMenuBackdrop") as? HTMLElement)?.style?.display = "block"
                })

                // Close button
                element.querySelector("#mobileMenuClose")?.addEventListener("click", {
                    (element.querySelector("#mobileMenuOverlay") as? HTMLElement)?.style?.display = "none"
                    (element.querySelector("#mobileMenuBackdrop") as? HTMLElement)?.style?.display = "none"
                })

                // Nav items
                element.querySelector("#mobileNavActivity")?.addEventListener("click", {
                    (element.querySelector("#mobileMenuOverlay") as? HTMLElement)?.style?.display = "none"
                    onNavClick?.invoke("Activity")
                })
                element.querySelector("#mobileNavDashboard")?.addEventListener("click", {
                    (element.querySelector("#mobileMenuOverlay") as? HTMLElement)?.style?.display = "none"
                    onNavClick?.invoke("Dashboard")
                })
                element.querySelector("#mobileNavGoals")?.addEventListener("click", {
                    (element.querySelector("#mobileMenuOverlay") as? HTMLElement)?.style?.display = "none"
                    onNavClick?.invoke("Goals")
                })
                element.querySelector("#mobileNavBudget")?.addEventListener("click", {
                    (element.querySelector("#mobileMenuOverlay") as? HTMLElement)?.style?.display = "none"
                    onNavClick?.invoke("Budget")
                })
                element.querySelector("#mobileNavSettings")?.addEventListener("click", {
                    (element.querySelector("#mobileMenuOverlay") as? HTMLElement)?.style?.display = "none"
                    onSettingsClick?.invoke()
                })
            }

            // Language toggle
            element.querySelector("#landing-language")?.addEventListener("click", {
                LocaleManager.toggleLocale()
                // Set flag so app stays on landing after reload
                window.localStorage.setItem("stay_on_landing", "true")
                window.location.reload()
            })

            // Header buttons - for logged out state
            element.querySelector("#login-link")?.addEventListener("click", { onLoginClick() })
            element.querySelector("#get-started-btn")?.addEventListener("click", { onGetStartedClick() })

            // Header button - for logged in state
            element.querySelector("#dashboard-link")?.addEventListener("click", { onDashboardClick() })

            // Hero CTAs
            element.querySelector("#hero-cta-primary")?.addEventListener("click", { onGetStartedClick() })
            element.querySelector("#hero-cta-secondary")?.addEventListener("click", { onGuideClick() })

            // Showcase: platform selector swaps the visible device frame + plays its
            // loop; the CTA reuses the get-started flow; each video is revealed only
            // once it actually loads (files may not exist yet → the placeholder stays).
            val scTabs = element.querySelectorAll(".showcase-tab")
            for (i in 0 until scTabs.length) {
                val tab = scTabs.item(i) as? HTMLElement ?: continue
                tab.addEventListener("click", {
                    val p = tab.getAttribute("data-sc")
                    val allTabs = element.querySelectorAll(".showcase-tab")
                    for (j in 0 until allTabs.length) {
                        val t = allTabs.item(j) as HTMLElement
                        val active = t.getAttribute("data-sc") == p
                        t.style.background = if (active) "#00d9ff" else "transparent"
                        t.style.color = if (active) "#0a0a0a" else "#99a0a6"
                    }
                    val frames = element.querySelectorAll(".showcase-frame")
                    for (j in 0 until frames.length) {
                        val f = frames.item(j) as HTMLElement
                        f.style.display = if (f.getAttribute("data-sc") == p) "block" else "none"
                    }
                    val vids = element.querySelectorAll(".sc-vid")
                    for (j in 0 until vids.length) {
                        val v = vids.item(j) as HTMLElement
                        if (v.getAttribute("data-sc") == p) runCatching { v.asDynamic().play() }
                    }
                })
            }
            element.querySelector("#showcase-cta")?.addEventListener("click", { onGetStartedClick() })
            val scVids = element.querySelectorAll(".sc-vid")
            for (i in 0 until scVids.length) {
                val v = scVids.item(i) as? HTMLElement ?: continue
                v.addEventListener("loadeddata", {
                    v.style.display = "block"
                    (v.parentElement?.querySelector(".sc-ph") as? HTMLElement)?.style?.display = "none"
                })
            }

            // Scroll-in animations for philosophy, features, pricing, trust, mystery
            js("""
                (function() {
                    var observer = new IntersectionObserver(function(entries) {
                        entries.forEach(function(entry) {
                            if (entry.isIntersecting) {
                                entry.target.classList.add('animate');
                                observer.unobserve(entry.target);
                            }
                        });
                    }, { threshold: 0.2 });
                    ['philosophy-grid', 'features-grid', 'pricing-grid', 'trust-grid'].forEach(function(id) {
                        var el = document.getElementById(id);
                        if (el) observer.observe(el);
                    });
                })();
            """)

            // Continuous marquee scroll for advice ribbon (pause + zoom on hover).
            // Re-init guard is critical: this callback fires on every landing-page
            // re-render (locale switch, theme change, login state change). Without
            // the guard, each rerun wraps the existing track inside another track,
            // doubling animation paths and causing the visible "speed-up" jumps.
            js("""
                (function() {
                    var ribbon = document.getElementById('advice-ribbon');
                    if (!ribbon) return;
                    if (ribbon.querySelector('.advice-track')) return; // already initialised
                    var cards = Array.prototype.slice.call(ribbon.children);
                    if (cards.length === 0) return;
                    var track = document.createElement('div');
                    track.className = 'advice-track';
                    cards.forEach(function(c) { track.appendChild(c); });
                    // Duplicate cards so the marquee loops seamlessly
                    cards.forEach(function(c) { track.appendChild(c.cloneNode(true)); });
                    ribbon.appendChild(track);
                })();
            """)

            // Generate trust-section particles converging into each item
            js("""
                (function() {
                    var grid = document.getElementById('trust-grid');
                    if (!grid) return;
                    var items = grid.querySelectorAll('.trust-item');
                    items.forEach(function(item, idx) {
                        // 8 particles per item, scattered at random offsets around the icon
                        for (var i = 0; i < 8; i++) {
                            var p = document.createElement('span');
                            p.className = 'trust-particle';
                            var angle = Math.random() * Math.PI * 2;
                            var dist = 80 + Math.random() * 120;
                            var px = Math.cos(angle) * dist;
                            var py = Math.sin(angle) * dist;
                            p.style.setProperty('--px', px + 'px');
                            p.style.setProperty('--py', py + 'px');
                            p.style.animationDelay = ((idx * 0.15) + (Math.random() * 0.3)) + 's';
                            item.appendChild(p);
                        }
                    });
                })();
            """)

            // Pricing CTAs (front and back faces)
            element.querySelector("#standard-cta")?.addEventListener("click", { (onSubscribeClick ?: onGetStartedClick)() })
            element.querySelector("#pro-cta")?.addEventListener("click", { (onSubscribeClick ?: onGetStartedClick)() })
            element.querySelector("#standard-cta-back")?.addEventListener("click", { (onSubscribeClick ?: onGetStartedClick)() })
            element.querySelector("#pro-cta-back")?.addEventListener("click", { (onSubscribeClick ?: onGetStartedClick)() })

            // Pricing toggle — true 3D flip revealing yearly on the back of the card
            js("""
                (function() {
                    function flipPricingCards(showYearly) {
                        var cards = document.querySelectorAll('#pricing-grid .pricing-flipper');
                        cards.forEach(function(c) {
                            if (showYearly) c.classList.add('flipped');
                            else c.classList.remove('flipped');
                        });
                    }
                    window.__flipPricingCards = flipPricingCards;
                })();
            """)

            // Pricing toggle — flips cards 180° and swaps monthly/yearly price at midpoint
            element.querySelector("#toggle-monthly")?.addEventListener("click", {
                (element.querySelector("#toggle-monthly") as? HTMLElement)?.let { it.style.background = "#00d9ff"; it.style.color = "#0a0a0a" }
                (element.querySelector("#toggle-yearly") as? HTMLElement)?.let { it.style.background = "transparent"; it.style.color = "#99a0a6" }
                js("if (window.__flipPricingCards) window.__flipPricingCards(false);")
            })
            element.querySelector("#toggle-yearly")?.addEventListener("click", {
                (element.querySelector("#toggle-yearly") as? HTMLElement)?.let { it.style.background = "#00d9ff"; it.style.color = "#0a0a0a" }
                (element.querySelector("#toggle-monthly") as? HTMLElement)?.let { it.style.background = "transparent"; it.style.color = "#99a0a6" }
                js("if (window.__flipPricingCards) window.__flipPricingCards(true);")
            })

            // Final CTA
            element.querySelector("#final-cta-btn")?.addEventListener("click", { (onSubscribeMenuClick ?: onSubscribeClick ?: onGetStartedClick)() })

            // Footer legal links
            element.querySelector("#footer-terms")?.addEventListener("click", { e ->
                e.preventDefault()
                onTermsClick()
            })
            element.querySelector("#footer-privacy")?.addEventListener("click", { e ->
                e.preventDefault()
                onPrivacyClick()
            })
            element.querySelector("#footer-security")?.addEventListener("click", { e ->
                e.preventDefault()
                onSecurityClick()
            })
            element.querySelector("#footer-about")?.addEventListener("click", { e ->
                e.preventDefault()
                onAboutClick()
            })
            element.querySelector("#footer-guide")?.addEventListener("click", { e ->
                e.preventDefault()
                onGuideClick()
            })
            element.querySelector("#footer-contact")?.addEventListener("click", { e ->
                e.preventDefault()
                onContactClick()
            })
            element.querySelector("#footer-team")?.addEventListener("click", { e ->
                e.preventDefault()
                onTeamClick()
            })

            // Logo click handlers - scroll to top
            element.querySelector("#header-logo")?.addEventListener("click", {
                window.scrollTo(0.0, 0.0)
            })
            element.querySelector("#footer-logo")?.addEventListener("click", {
                window.scrollTo(js("{ top: 0, behavior: 'smooth' }"))
            })

            // Scroll listener for logo resize (desktop only - mobile uses text logo)
            if (!isMobile) {
                val headerLogo = element.querySelector("#header-logo") as? HTMLElement
                val header = element.querySelector("#landing-header") as? HTMLElement
                window.addEventListener("scroll", {
                    val scrollY = window.scrollY
                    if (headerLogo != null && header != null) {
                        if (scrollY > 50) {
                            headerLogo.style.height = "32px"
                            header.style.padding = "8px 48px"
                        } else {
                            headerLogo.style.height = "56px"
                            header.style.padding = "16px 48px"
                        }
                    }
                })
            }

            // Hamburger menu (desktop)
            if (!isMobile) {
                MarketingMenu.attachEventListeners(
                    onNavigate = { page ->
                        when (page) {
                            "guide" -> onGuideClick()
                            "team" -> onTeamClick()
                            "about" -> onAboutClick()
                            "contact" -> onContactClick()
                            "subscribe" -> (onSubscribeMenuClick ?: onSubscribeClick ?: onGetStartedClick)()
                            "privacy" -> onPrivacyClick()
                            "terms" -> onTermsClick()
                            "security" -> onSecurityClick()
                        }
                    },
                    isLoggedIn = isLoggedIn,
                    onAppNavigate = if (isLoggedIn) { tab ->
                        when (tab) {
                            "transactions" -> onNavClick?.invoke("Activity")
                            "dashboard" -> onNavClick?.invoke("Dashboard")
                            "goals" -> onNavClick?.invoke("Goals")
                            "budget" -> onNavClick?.invoke("Budget")
                            "settings" -> onSettingsClick?.invoke()
                            "logout" -> onLogout?.invoke()
                        }
                    } else null,
                )
            }

            // Animate the hero percentage
            animatePercentage(element)
        }, 0)
    }

    private fun animatePercentage(element: HTMLElement) {
        val percentageEl = element.querySelector("#hero-percentage") as? HTMLElement ?: return
        val containerEl = element.querySelector("#hero-pct-container") as? HTMLElement ?: return
        val progressFill = element.querySelector("#hero-progress-fill") as? HTMLElement
        var current = -5 // Start from -5 to show the yellow phase
        val target = 20
        val stepTime = 90 // Slower to appreciate the gradient

        val fontSize = containerEl.style.fontSize
        val baseStyle = "font-size: $fontSize; font-weight: 800; margin-bottom: 0; "

        fun updateGradientStyle(pct: Int) {
            // Progress bar: grows from 0% to 100% over -5 → target range
            val barPct = ((pct + 5.0) / (target + 5.0) * 100).coerceIn(0.0, 100.0).toInt()

            if (pct <= 0) {
                containerEl.setAttribute("style",
                    baseStyle + "color: #888; text-shadow: none;")
                progressFill?.setAttribute("style",
                    "width: ${barPct}%; height: 100%; border-radius: 2px; background: #888;")
            } else if (pct >= 15) {
                val glowIntensity = ((pct - 15.0) / 5.0).coerceAtMost(1.0)
                val t = glowIntensity * 0.4
                val r = (0 + (255 - 0) * t).toInt()
                val g = (217 + (255 - 217) * t).toInt()
                val hex = "#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}ff"
                val glowPx = (glowIntensity * 12 + 10).toInt()   // capped: 30→60px spiked the GPU near 20%
                containerEl.setAttribute("style",
                    baseStyle + "color: $hex; text-shadow: 0 0 ${glowPx}px rgba(0, 217, 255, 0.5);")
                progressFill?.setAttribute("style",
                    "width: ${barPct}%; height: 100%; border-radius: 2px; background: $hex; box-shadow: 0 0 ${glowPx / 3}px $hex;")
            } else {
                // 0 → 15: narrow gradient band sweeps left to right
                val progress = (pct.toDouble() / 15.0).coerceIn(0.0, 1.0)
                val bandWidth = 12
                val bandCenter = (progress * 100).toInt()
                val bandStart = (bandCenter - bandWidth / 2).coerceIn(0, 100)
                val bandEnd = (bandCenter + bandWidth / 2).coerceIn(0, 100)
                containerEl.setAttribute("style",
                    baseStyle + "background: linear-gradient(to right, #00D9FF ${bandStart}%, #888 ${bandEnd}%); " +
                    "-webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;")
                progressFill?.setAttribute("style",
                    "width: ${barPct}%; height: 100%; border-radius: 2px; background: linear-gradient(to right, #00D9FF ${bandStart}%, #888 ${bandEnd}%);")
            }
        }

        fun animate() {
            if (current <= target) {
                percentageEl.textContent = current.toString()
                updateGradientStyle(current)
                current++
                window.setTimeout({ animate() }, stepTime)
            }
        }

        // Start animation after a short delay
        window.setTimeout({ animate() }, 500)
    }
}
